import { pageTitle } from '@palaccount/config';
import type { PaymentTermMethod, PaymentTermResponse } from '@palaccount/contracts';
import { Check, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { localizeServerMessage } from '@/lib/server-messages';

import { usePaymentTermMutations, usePaymentTerms } from './payment-terms-api';

/** MD-09 §5 field 2 — the four ways of working, in the spec's own order. */
const METHODS: readonly PaymentTermMethod[] = [
  'immediate',
  'days_after',
  'end_of_next_month',
  'day_of_month',
];

interface TermFormState {
  name: string;
  method: PaymentTermMethod;
  days: string;
  dayOfMonth: string;
  earlyDiscountPct: string;
  discountDays: string;
  defaultForCustomers: boolean;
  defaultForVendors: boolean;
}

const EMPTY: TermFormState = {
  name: '',
  method: 'days_after',
  days: '30',
  dayOfMonth: '15',
  earlyDiscountPct: '',
  discountDays: '',
  defaultForCustomers: false,
  defaultForVendors: false,
};

const INT_RE = /^\d{1,3}$/;
const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/;

/**
 * MD-09 — the payment-terms screen: list + form under General Settings (§3).
 * "A short screen — most companies define three or four terms and never touch
 * it again." Columns per §4: Name, How it works, Days, status.
 */
export function PaymentTermsPage() {
  const { t } = useTranslation();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<PaymentTermResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [values, setValues] = useState<TermFormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('payterms.title'));
  }, [t]);

  const { data: terms, isPending } = usePaymentTerms(showInactive);
  const { create, update, setActive, remove } = usePaymentTermMutations();

  const openNew = () => {
    setEditing(null);
    setValues(EMPTY);
    setError(null);
    setFormOpen(true);
  };
  const openEdit = (term: PaymentTermResponse) => {
    setEditing(term);
    setValues({
      name: term.name,
      method: term.method,
      days: term.days === null ? '' : String(term.days),
      dayOfMonth: term.dayOfMonth === null ? '' : String(term.dayOfMonth),
      earlyDiscountPct: Number(term.earlyDiscountPct) === 0 ? '' : term.earlyDiscountPct,
      discountDays: term.discountDays === null ? '' : String(term.discountDays),
      defaultForCustomers: term.defaultForCustomers,
      defaultForVendors: term.defaultForVendors,
    });
    setError(null);
    setFormOpen(true);
  };

  const fail = (cause: unknown) =>
    setError(localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause)));

  const submit = async () => {
    setError(null);
    if (values.name.trim() === '') return setError(t('payterms.errName'));
    // ERR-MD-09-01: "Days must be between 0 and 365."
    if (values.method === 'days_after') {
      if (!INT_RE.test(values.days.trim()) || Number(values.days) > 365) {
        return setError(t('payterms.errDays'));
      }
    }
    if (values.method === 'day_of_month') {
      const day = Number(values.dayOfMonth.trim());
      if (!INT_RE.test(values.dayOfMonth.trim()) || day < 1 || day > 31) {
        return setError(t('payterms.errDayOfMonth'));
      }
    }
    const discount = values.earlyDiscountPct.trim();
    if (discount !== '' && !PCT_RE.test(discount)) return setError(t('payterms.errPct'));
    const hasDiscount = discount !== '' && Number(discount) !== 0;
    // ERR-MD-09-02: "Say how many days count as early."
    if (hasDiscount && (!INT_RE.test(values.discountDays.trim()) || Number(values.discountDays) < 1)) {
      return setError(t('payterms.errDiscountDays'));
    }
    const body = {
      name: values.name.trim(),
      method: values.method,
      ...(values.method === 'days_after' ? { days: Number(values.days) } : {}),
      ...(values.method === 'day_of_month' ? { dayOfMonth: Number(values.dayOfMonth) } : {}),
      ...(hasDiscount
        ? { earlyDiscountPct: discount, discountDays: Number(values.discountDays) }
        : { earlyDiscountPct: '0' }),
      defaultForCustomers: values.defaultForCustomers,
      defaultForVendors: values.defaultForVendors,
    };
    try {
      if (editing) await update.mutateAsync({ termId: editing.id, body });
      else await create.mutateAsync(body);
      setFormOpen(false);
    } catch (cause) {
      fail(cause);
    }
  };

  /** §6: Delete — hidden when any customer or vendor uses the term. */
  const removeTerm = async (term: PaymentTermResponse) => {
    // MD-RULE-3 / §4.20: a delete, even of an unused record, asks first — and
    // it asks GEN-58's sentence in the shared box (GEN-204/GEN-209), which the
    // button raises. This runs after the person has said Delete.
    setDeleting(false);
    try {
      await remove.mutateAsync({ termId: term.id });
      setFormOpen(false);
    } catch (cause) {
      fail(cause);
    }
  };

  const methodLabel = (method: PaymentTermMethod) => t(`payterms.method.${method}`);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('payterms.title')}</h2>
        <Gated permission="accounting.create" reasonKey="perm.cannot.createPayTerms">
          <Button onClick={openNew}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>

      {formOpen ? (
        <Card>
          <CardHeader>
            <CardTitle>{t(editing ? 'payterms.editTitle' : 'payterms.newTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="pt-name">{t('payterms.fName')}</Label>
                <Input
                  id="pt-name"
                  maxLength={60}
                  value={values.name}
                  onChange={(event) => setValues({ ...values, name: event.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="pt-method">{t('payterms.fMethod')}</Label>
                <NativeSelect
                  id="pt-method"
                  value={values.method}
                  onChange={(event) =>
                    setValues({ ...values, method: event.target.value as PaymentTermMethod })
                  }
                >
                  {METHODS.map((method) => (
                    <option key={method} value={method}>
                      {methodLabel(method)}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              {values.method === 'days_after' ? (
                <div className="grid gap-1.5">
                  <Label htmlFor="pt-days">{t('payterms.fDays')}</Label>
                  <Input
                    id="pt-days"
                    dir="ltr"
                    inputMode="numeric"
                    className="font-mono"
                    value={values.days}
                    onChange={(event) => setValues({ ...values, days: event.target.value })}
                  />
                </div>
              ) : null}
              {values.method === 'day_of_month' ? (
                <div className="grid gap-1.5">
                  <Label htmlFor="pt-dom">{t('payterms.fDayOfMonth')}</Label>
                  <Input
                    id="pt-dom"
                    dir="ltr"
                    inputMode="numeric"
                    className="font-mono"
                    value={values.dayOfMonth}
                    onChange={(event) => setValues({ ...values, dayOfMonth: event.target.value })}
                  />
                </div>
              ) : null}
              <div className="grid gap-1.5">
                <Label htmlFor="pt-discount">{t('payterms.fDiscount')}</Label>
                <Input
                  id="pt-discount"
                  dir="ltr"
                  inputMode="decimal"
                  className="font-mono"
                  placeholder="0.00"
                  value={values.earlyDiscountPct}
                  onChange={(event) => setValues({ ...values, earlyDiscountPct: event.target.value })}
                />
              </div>
              {values.earlyDiscountPct.trim() !== '' && Number(values.earlyDiscountPct) !== 0 ? (
                <div className="grid gap-1.5">
                  <Label htmlFor="pt-discount-days">{t('payterms.fDiscountDays')}</Label>
                  <Input
                    id="pt-discount-days"
                    dir="ltr"
                    inputMode="numeric"
                    className="font-mono"
                    value={values.discountDays}
                    onChange={(event) => setValues({ ...values, discountDays: event.target.value })}
                  />
                </div>
              ) : null}
            </div>
            {/* Fields 7–8 (BR-MD-09-06): ticking here unticks the previous default. */}
            <Label className="flex items-center gap-2 font-normal">
              <Checkbox
                checked={values.defaultForCustomers}
                onCheckedChange={(checked) =>
                  setValues({ ...values, defaultForCustomers: checked === true })
                }
              />
              {t('payterms.fDefaultCustomers')}
            </Label>
            <Label className="flex items-center gap-2 font-normal">
              <Checkbox
                checked={values.defaultForVendors}
                onCheckedChange={(checked) =>
                  setValues({ ...values, defaultForVendors: checked === true })
                }
              />
              {t('payterms.fDefaultVendors')}
            </Label>
            <div className="flex flex-wrap gap-2">
              <Gated
                permission={editing ? 'accounting.edit' : 'accounting.create'}
                reasonKey={editing ? 'perm.cannot.editPayTerms' : 'perm.cannot.createPayTerms'}
              >
                <Button onClick={() => void submit()} disabled={!values.name.trim()}>
                  {t('common.save')}
                </Button>
              </Gated>
              <Button variant="outline" onClick={() => setFormOpen(false)}>
                {t('common.cancel')}
              </Button>
              {editing ? (
                <Gated permission="accounting.edit" reasonKey="perm.cannot.editPayTerms">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setActive
                        .mutateAsync({ termId: editing.id, active: !editing.isActive })
                        .then(() => setFormOpen(false))
                        .catch(fail);
                    }}
                  >
                    {t(editing.isActive ? 'items.switchOff' : 'items.switchOn')}
                  </Button>
                </Gated>
              ) : null}
              {/* §6: Delete hidden when any customer, vendor or document uses the term. */}
              {editing && editing.partnersOn === 0 ? (
                <>
                  <Gated permission="accounting.delete" reasonKey="perm.cannot.deletePayTerms">
                    <Button variant="destructive" onClick={() => setDeleting(true)}>
                      {t('common.delete')}
                    </Button>
                  </Gated>
                  <DeleteConfirmBox
                    open={deleting}
                    detail={t('payterms.confirmDelete', { name: editing.name })}
                    onCancel={() => setDeleting(false)}
                    onConfirm={() => void removeTerm(editing)}
                    busy={remove.isPending}
                  />
                </>
              ) : null}
            </div>
            {editing ? <AuthorshipLine record={editing} /> : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="grid gap-4">
          <Label className="flex items-center gap-2 text-sm font-normal">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            {t('common.showInactive')}
          </Label>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : (terms ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('payterms.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('payterms.colName')}</TableHead>
                  <TableHead>{t('payterms.colMethod')}</TableHead>
                  <TableHead className="text-end">{t('payterms.colDays')}</TableHead>
                  <TableHead>{t('payterms.colDefault')}</TableHead>
                  <TableHead className="text-end">{t('payterms.colPartners')}</TableHead>
                  <TableHead>{t('payterms.colStatus')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(terms ?? []).map((term) => (
                  <TableRow key={term.id} className="cursor-pointer" onClick={() => openEdit(term)}>
                    <TableCell className="font-medium">{term.name}</TableCell>
                    <TableCell>{methodLabel(term.method)}</TableCell>
                    <TableCell className="text-end font-mono">
                      {term.method === 'days_after'
                        ? term.days
                        : term.method === 'day_of_month'
                          ? term.dayOfMonth
                          : '—'}
                    </TableCell>
                    <TableCell>
                      {term.defaultForCustomers || term.defaultForVendors ? (
                        <Check className="size-4" aria-label={t('common.yes')} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-end font-mono">{term.partnersOn}</TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {t(term.isActive ? 'common.active' : 'common.inactive')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
